import { pick } from "lodash";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { FormatedFields } from "../types/fields.types";
import { sanitizeData } from "../utils/sanitizeFormData";
import { useStore } from "../store/store";
import { useUpdateRecord } from "./useUpdateRecord";
import { MutationOptions } from "../types/mutation.types";
import { Notifications } from "../store/store.types";
import { UseMutationResult } from "@tanstack/react-query";
import { ErrorResponsesCreateOrUpdate, Record as UpdateRecord } from "../types/records.types";

export const useUpdateRecordAndRelatedRecord = (options?: MutationOptions) => {
	const { notifications } = useStore();
	const updateRecord = useUpdateRecord({ ...options, skipOptimisticUpdate: true });
	
	const updateRecordAndRelatedRecord = <EntityType, RelatedEntityType>(
		entity: string,
	    formData: unknown,
		selectedEntityData: EntityType,
		entityFormatedFields: FormatedFields,
		relatedEntity: string,
		relatedEntityFormatedFields: FormatedFields,
		entityForm: UseFormReturn<FieldValues>,
	) => {
		const typedFormData = formData as EntityType & FieldValues;
		type EntityTypeWithId = EntityType & { id: number };

		const { entityDirtyData, relatedEntityDirtyData } = getEntityAndRelatedEntityDirtyData<
			EntityType & FieldValues,
			string
		>(entityForm, typedFormData, relatedEntity);

		const entityFieldNames = entityFormatedFields.map((field) => field.field);
		const filteredEntityDirtyData = pick(entityDirtyData, entityFieldNames);
		const sanitizedEntityData = sanitizeData(filteredEntityDirtyData, entityFormatedFields);

		const sanitizedRelatedEntityData = relatedEntityDirtyData.map((item: Partial<RelatedEntityType>) =>
			sanitizeData(item, relatedEntityFormatedFields)
		);

		const relatedEntityData = selectedEntityData[relatedEntity as keyof EntityType] as Array<RelatedEntityType>;

		const relatedEntityIdsToDelete = getRelatedEntityIdsToDelete(
			relatedEntityData as Array<{ id: number }>,
			typedFormData[relatedEntity]
		);

		editData(
			sanitizedEntityData as Partial<EntityTypeWithId>,
			selectedEntityData as EntityTypeWithId,
			entity,
			sanitizedRelatedEntityData,
			relatedEntity,
			relatedEntityIdsToDelete,
			notifications,
			updateRecord
		);
	};

	return updateRecordAndRelatedRecord;
};

const getEntityAndRelatedEntityDirtyData = <T extends FieldValues, K extends keyof T>(
	entityForm: UseFormReturn<FieldValues>,
	data: T,
	relatedEntity: K
) => {
	const dirtyFields = entityForm.formState.dirtyFields;

	const entityDirtyFields = Object.keys(dirtyFields).filter((key) => key !== relatedEntity);
	const entityDirtyData = pick(data, entityDirtyFields);

	const relatedEntityDirtyFields = dirtyFields[relatedEntity as string];

	if (!Array.isArray(relatedEntityDirtyFields)) {
		return { entityDirtyData, relatedEntityDirtyData: [] };
	}

	const relatedEntityFormData = data[relatedEntity] as T[K];

	const relatedEntityDirtyData = relatedEntityFormData.flatMap((item: Record<string, any>, index: number) => {
		const dirtyFieldsForItem = relatedEntityDirtyFields[index];
		if (!dirtyFieldsForItem) return [];

		const filteredEntries = Object.entries(item).filter(([key]) => dirtyFieldsForItem[key] || key === "id");
		if (filteredEntries.length <= 1) return [];
		return [Object.fromEntries(filteredEntries)];
	});

	return { entityDirtyData, relatedEntityDirtyData };
};

const getRelatedEntityIdsToDelete = (relatedEntityData: Array<{ id: number }>, relatedEntityFormData: Array<any>) => {
	const relatedEntityEntriesIds = relatedEntityData?.map((item) => item.id);
	const relatedEntityIdsToDelete = relatedEntityEntriesIds?.filter(
		(id) => !relatedEntityFormData.some((item) => item.id === id)
	);
	return relatedEntityIdsToDelete;
};

const editData = async <T extends { id: number }, U extends { id?: number }>(
	sanitizedEntityData: Partial<T>,
	selectedElementEntityData: T,
	entity: string,
	sanitizedRelatedEntityData: Array<U>,
	relatedEntity: string,
	relatedEntityIdsToDelete: Array<number> | undefined,
	notifications: Notifications,
	updateRecord: UseMutationResult<any, ErrorResponsesCreateOrUpdate, UpdateRecord, any>,
) => {
	try {
		const relatedDataToCreate = [];
		const relatedDataToUpdate = [];
		const relatedDataIdsToDelete = [];

		for (const element of sanitizedRelatedEntityData) {
			if (element?.id) {
				relatedDataToUpdate.push(element);
			} else {
				relatedDataToCreate.push(element);
			}
		}
		if (relatedEntityIdsToDelete && relatedEntityIdsToDelete?.length > 0) {
			for (const elementId of relatedEntityIdsToDelete) {
				if (!elementId) return;
				relatedDataIdsToDelete.push(elementId);
			}
		}

		const payload = {
			entity: entity,
			id: selectedElementEntityData.id,
			data: {
				...sanitizedEntityData,
				[relatedEntity]: {
					create: relatedDataToCreate,
					update: relatedDataToUpdate,
					delete: relatedDataIdsToDelete,
				},
			},
		};
		await updateRecord.mutateAsync(payload);
		notifications.setNotification({
			isOpen: true,
			message: "Dados atualizados com sucesso.",
			severity: "success",
		});
	} catch (error) {
		const axiosError = error as any;
		const errorMessage = 
			axiosError?.response?.data?.errors?.[0]?.message ||
			"Erro ao atualizar dados.";
		notifications.setNotification({
			isOpen: true,
			message: errorMessage,
			severity: "error",
		});
	}
};
